import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { onAuthChange, logout } from '../firebase/auth';
import { db } from '../firebase/config';
import { doc, onSnapshot, setDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { queryUserByEmail } from '../firebase/firestore';
import { saStartSession, saEndSession } from '../utils/tracker';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [firebaseUser, setFirebaseUser] = useState(undefined); // undefined = loading
  const [userProfile, setUserProfile]   = useState(null);
  const [profileLoading, setProfileLoading] = useState(true); // true until profile is resolved
  const profileUnsubRef = useRef(null);
  const sessionStartedRef = useRef(false); // prevent double-start on profile re-syncs

  // End session when tab/window closes
  useEffect(() => {
    const onUnload = () => { saEndSession(); };
    window.addEventListener('beforeunload', onUnload);
    return () => window.removeEventListener('beforeunload', onUnload);
  }, []);

  useEffect(() => {
    const unsub = onAuthChange(user => {
      // Clean up previous profile listener
      if (profileUnsubRef.current) {
        profileUnsubRef.current();
        profileUnsubRef.current = null;
      }

      setFirebaseUser(user);

      if (!user) {
        // User signed out — end the session
        if (sessionStartedRef.current) {
          saEndSession();
          sessionStartedRef.current = false;
        }
        setUserProfile(null);
        setProfileLoading(false);
        return;
      }

      // Start loading profile
      setProfileLoading(true);

      // Real-time sync of user's Firestore profile
      const unsub2 = onSnapshot(doc(db, 'users', user.uid), async snap => {
        if (snap.exists()) {
          const profile = { id: snap.id, ...snap.data() };

          // If the profile is marked removed (by admin or super admin), sign the
          // user out immediately — this is the real-time force-logout mechanism.
          // The onSnapshot fires the instant the Firestore doc is updated, so
          // the kicked user sees the login screen within a second.
          // Exception: superadmins cannot be locked out this way — their account
          // is platform-level and must always remain accessible via the super
          // admin portal regardless of any company-level removal action.
          if (profile.status === 'removed' && profile.role !== 'superadmin') {
            logout().catch(() => {});
            setProfileLoading(false);
            return;
          }

          setUserProfile(profile);
          // Start tracking session once (first time profile resolves for this auth session)
          if (!sessionStartedRef.current) {
            sessionStartedRef.current = true;
            saStartSession(user.uid, profile.name, profile.email, profile.role, profile.companyId || 'default');
          }
          // Self-heal: this profile doc already exists but has no companyId —
          // e.g. they signed up once before a pre-registration stub existed
          // for their email, or before the email-casing fix went live, and got
          // permanently stuck. Skip this for removed accounts — they shouldn't
          // be re-linked automatically. The admin must re-invite them explicitly.
          if (!profile.companyId && profile.role !== 'superadmin' && profile.status !== 'removed') {
            (async () => {
              try {
                const preReg = await queryUserByEmail(profile.email || user.email || '');
                if (preReg && preReg.companyId) {
                  await setDoc(doc(db, 'users', user.uid), {
                    companyId: preReg.companyId,
                    role: preReg.role || profile.role || 'counsellor',
                  }, { merge: true });
                  if (preReg.id && preReg.id !== user.uid) {
                    await deleteDoc(doc(db, 'users', preReg.id));
                  }
                }
              } catch { /* ignore — rules may not allow yet, or no match found */ }
            })();
          }
        } else {
          // New user — check if admin pre-registered this email. This is the ONLY
          // legitimate way a user gets linked to a company: SuperAdminPortal (new
          // company signup) and Settings team-invite both create a stub `users`
          // doc with a `companyId` BEFORE the person ever signs in via Firebase
          // Auth. That stub has a random doc ID (not yet the real Firebase UID),
          // so once they do sign in we look it up by email and adopt its fields.
          const AVATAR_COLORS = ['#7c3aed','#3b82f6','#06b6d4','#10b981','#f59e0b','#ef4444','#ec4899'];
          const bg = AVATAR_COLORS[Math.floor(Math.random() * AVATAR_COLORS.length)];

          let role = 'counsellor';
          let companyId = null; // stays null unless a pre-registration proves otherwise
          let preRegId = null;

          try {
            const preReg = await queryUserByEmail(user.email || '');
            if (preReg) {
              role = preReg.role || 'counsellor';
              // CRITICAL: this companyId must be carried over. Previously it was
              // dropped here, so every new sign-in — invited or not — ended up
              // with no companyId at all, and CRMContext's `|| 'default'` fallback
              // silently lumped every company's users, leads, follow-ups, forms,
              // and settings into one shared bucket. That was the cross-company
              // data leak. Never default this to a shared value — null means
              // "not provisioned," and the app must block, not guess.
              companyId = preReg.companyId || null;
              preRegId = preReg.id;
            }
          } catch { /* ignore — rules may not allow yet */ }

          const newProfile = {
            name: user.displayName || user.email?.split('@')[0] || 'User',
            email: user.email || '',
            role,
            companyId,
            avatar: { bg, color: '#fff' },
            createdAt: serverTimestamp(),
          };
          try {
            await setDoc(doc(db, 'users', user.uid), newProfile, { merge: true });
            // Remove the old pre-registered stub (had a random doc ID, now replaced by UID-keyed doc)
            if (preRegId && preRegId !== user.uid) {
              await deleteDoc(doc(db, 'users', preRegId));
            }
          } catch {
            setUserProfile({ id: user.uid, ...newProfile });
          }
        }
        setProfileLoading(false); // profile is resolved (either from Firestore or fallback)
      }, () => {
        // On error, still unblock the UI with a fallback
        setUserProfile({
          id: user.uid,
          name: user.displayName || user.email,
          email: user.email,
          role: 'admin',
          avatar: { bg: '#7c3aed', color: '#fff' },
        });
        setProfileLoading(false);
      });

      profileUnsubRef.current = unsub2;
    });
    return () => {
      unsub();
      if (profileUnsubRef.current) profileUnsubRef.current();
    };
  }, []);

  const loading = firebaseUser === undefined;

  return (
    <AuthContext.Provider value={{ firebaseUser, userProfile, profileLoading, loading }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
